import { hourTicks, stepForSpacing, TICK_STEPS } from './charts.ts';
import { trackChartPointer } from './chartPointer.ts';
import { gridLineBudget, gridTicks, tickDecimals } from './lib/gridTicks.ts';
import { positionChartTip } from './chartTip.ts';
import { cardinal } from './types.ts';
import type { WavePoint } from './sources/waves.ts';

/**
 * Predicted waves for one station over the next two days.
 *
 * Kept in its own section with its own time axis rather than folded into the
 * observation stack above it: those charts share one domain over the last 24
 * hours, and stretching that to cover 48 hours ahead would squeeze every
 * observed series into the left third of its panel.
 *
 * Total sea leads; swell is drawn behind it, because the gap between them is
 * what separates a long groundswell from short local windsea at the same height.
 */

const W = 336;
const PLOT_H = 76;
const PAD = { top: 14, right: 12, bottom: 20, left: 34 };
const STRIP_H = 30;

const INK_MUTED = '#898781';
const INK_SECONDARY = '#c3c2b7';
const INK_PRIMARY = '#ffffff';
const GRID = '#2c2c2a';
const AXIS = '#383835';
const SURFACE = '#1a1a19';
const SERIES_1 = '#3987e5';

const SVG_NS = 'http://www.w3.org/2000/svg';

function el(name: string, attrs: Record<string, string | number> = {}): SVGElement {
  const node = document.createElementNS(SVG_NS, name);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

export function waveAt(points: WavePoint[], at: Date): WavePoint | null {
  if (points.length === 0) return null;
  return points.reduce((best, p) =>
    Math.abs(p.t.getTime() - at.getTime()) < Math.abs(best.t.getTime() - at.getTime()) ? p : best,
  );
}

/** A rounded ceiling that always clears the data. */
export function waveCeiling(points: WavePoint[]): number {
  const peak = Math.max(...points.map((p) => p.heightFt), 1);
  if (peak <= 4) return 4;
  if (peak <= 8) return 8;
  if (peak <= 12) return 12;
  return Math.ceil(peak / 5) * 5;
}

export function renderWaveForecast(
  host: HTMLElement,
  points: WavePoint[],
  now: Date = new Date(),
): void {
  host.textContent = '';
  if (points.length < 2) return;

  const innerW = W - PAD.left - PAD.right;
  const t0 = points[0].t.getTime();
  const t1 = points[points.length - 1].t.getTime();
  const span = Math.max(t1 - t0, 1);
  const top = waveCeiling(points);

  const xAt = (ms: number) => PAD.left + ((ms - t0) / span) * innerW;
  const yAt = (ft: number) => PAD.top + (1 - Math.min(ft, top) / top) * PLOT_H;

  const totalH = PAD.top + PLOT_H + PAD.bottom + STRIP_H;
  const svg = el('svg', {
    viewBox: `0 0 ${W} ${totalH}`,
    width: '100%',
    height: totalH,
    role: 'img',
    'aria-label': 'Predicted wave height, period and direction for the next 48 hours',
  }) as SVGSVGElement;

  // --- Frame ---
  const ticks = gridTicks(0, top, gridLineBudget(PLOT_H));
  const decimals = tickDecimals(ticks);
  for (const v of ticks) {
    svg.append(el('line', {
      x1: PAD.left, x2: W - PAD.right, y1: yAt(v), y2: yAt(v),
      stroke: v === 0 ? AXIS : GRID, 'stroke-width': 1,
    }));
    const label = el('text', {
      x: PAD.left - 6, y: yAt(v) + 3.5,
      'text-anchor': 'end', fill: INK_MUTED, 'font-size': 9,
      'font-variant-numeric': 'tabular-nums',
    });
    label.textContent = v.toFixed(decimals);
    svg.append(label);
  }

  // --- Hour ticks, same rules as every other chart here ---
  const tickStep = stepForSpacing(span, innerW, 7, TICK_STEPS);
  const labelStep = stepForSpacing(span, innerW, 46, TICK_STEPS.filter((h) => h >= tickStep));

  for (const ms of hourTicks(t0, t1, tickStep)) {
    const tx = xAt(ms);
    const at = new Date(ms);
    const labelled = at.getHours() % labelStep === 0;

    if (labelled) {
      svg.append(el('line', {
        x1: tx, x2: tx, y1: PAD.top, y2: PAD.top + PLOT_H,
        stroke: GRID, 'stroke-width': 1,
      }));
    }
    svg.append(el('line', {
      x1: tx, x2: tx,
      y1: PAD.top + PLOT_H, y2: PAD.top + PLOT_H + (labelled ? 6 : 3),
      stroke: INK_MUTED, 'stroke-width': 1, opacity: labelled ? 1 : 0.75,
    }));

    if (!labelled) continue;
    const text = el('text', {
      x: tx, y: PAD.top + PLOT_H + 15,
      'text-anchor': 'middle', fill: INK_MUTED, 'font-size': 9,
    });
    text.textContent = at.getHours() === 0
      ? new Intl.DateTimeFormat([], { weekday: 'short' }).format(at)
      : new Intl.DateTimeFormat([], { hour: 'numeric' }).format(at);
    svg.append(text);
  }

  // --- Swell behind, total sea in front ---
  const swell = points.filter((p) => p.swellFt !== null);
  if (swell.length > 1) {
    svg.append(el('polyline', {
      points: swell.map((p) => `${xAt(p.t.getTime())},${yAt(p.swellFt!)}`).join(' '),
      fill: 'none', stroke: INK_MUTED, 'stroke-width': 1.5, 'stroke-linejoin': 'round',
    }));
  }

  const line = points.map((p) => `${xAt(p.t.getTime())},${yAt(p.heightFt)}`).join(' ');
  svg.append(el('polygon', {
    points: `${xAt(t0)},${yAt(0)} ${line} ${xAt(t1)},${yAt(0)}`,
    fill: SERIES_1, 'fill-opacity': 0.16,
  }));
  svg.append(el('polyline', {
    points: line, fill: 'none', stroke: SERIES_1, 'stroke-width': 2,
    'stroke-linejoin': 'round',
  }));

  // --- Direction and period, sampled on the hour grid ---
  const stripStep = stepForSpacing(span, innerW, 40, TICK_STEPS.filter((h) => h >= tickStep));
  const arrowY = PAD.top + PLOT_H + PAD.bottom + 8;

  for (const ms of hourTicks(t0, t1, stripStep)) {
    const p = waveAt(points, new Date(ms));
    if (!p) continue;
    const cx = xAt(ms);

    if (p.dirDeg !== null) {
      svg.append(el('path', {
        d: 'M0,-4.5 L3,2.5 L0,0.8 L-3,2.5 Z',
        fill: INK_SECONDARY,
        // Wave direction is already the way they travel, unlike wind bearings.
        transform: `translate(${cx},${arrowY}) rotate(${p.dirDeg})`,
      }));
    }
    if (p.periodS !== null) {
      const period = el('text', {
        x: cx, y: arrowY + 15,
        'text-anchor': 'middle', fill: INK_MUTED, 'font-size': 8,
        'font-variant-numeric': 'tabular-nums',
      });
      period.textContent = `${Math.round(p.periodS)}s`;
      svg.append(period);
    }
  }

  const stripLabel = el('text', {
    x: PAD.left - 6, y: arrowY + 4,
    'text-anchor': 'end', fill: INK_MUTED, 'font-size': 8,
  });
  stripLabel.textContent = 'dir';
  svg.append(stripLabel);

  // --- Now ---
  const nowMs = now.getTime();
  if (nowMs >= t0 && nowMs <= t1) {
    const nx = xAt(nowMs);
    svg.append(el('line', {
      x1: nx, x2: nx, y1: PAD.top, y2: PAD.top + PLOT_H,
      stroke: INK_PRIMARY, 'stroke-width': 1, opacity: 0.55,
    }));
    const nowLabel = el('text', {
      x: Math.min(Math.max(nx, PAD.left + 12), W - PAD.right - 12),
      y: PAD.top - 4, 'text-anchor': 'middle', fill: INK_MUTED, 'font-size': 8,
    });
    nowLabel.textContent = 'now';
    svg.append(nowLabel);
  }

  // --- Hover ---
  const crosshair = el('line', {
    y1: PAD.top, y2: PAD.top + PLOT_H,
    stroke: INK_MUTED, 'stroke-width': 1, opacity: 0,
  });
  const cursor = el('circle', { r: 3.5, fill: SERIES_1, stroke: SURFACE, 'stroke-width': 2, opacity: 0 });
  svg.append(crosshair, cursor);

  const wrap = document.createElement('div');
  wrap.className = 'chart-wrap';
  const tip = document.createElement('div');
  tip.className = 'chart-tip';
  tip.hidden = true;
  wrap.append(svg, tip);

  const tipFmt = new Intl.DateTimeFormat([], {
    weekday: 'short', hour: 'numeric', minute: '2-digit',
  });

  const showAt = (clientX: number) => {
    const box = svg.getBoundingClientRect();
    const px = ((clientX - box.left) / box.width) * W;
    const frac = Math.min(Math.max((px - PAD.left) / innerW, 0), 1);
    const p = waveAt(points, new Date(t0 + frac * span));
    if (!p) return;

    const tx = xAt(p.t.getTime());
    crosshair.setAttribute('x1', String(tx));
    crosshair.setAttribute('x2', String(tx));
    crosshair.setAttribute('opacity', '1');
    cursor.setAttribute('cx', String(tx));
    cursor.setAttribute('cy', String(yAt(p.heightFt)));
    cursor.setAttribute('opacity', '1');

    tip.innerHTML =
      `<strong>${tipFmt.format(p.t)}</strong>` +
      `<span>${p.heightFt.toFixed(1)} ft combined</span>` +
      (p.swellFt !== null ? `<span>swell ${p.swellFt.toFixed(1)} ft</span>` : '') +
      (p.periodS !== null ? `<span>${p.periodS.toFixed(0)} s period</span>` : '') +
      (p.dirDeg !== null ? `<span>toward ${cardinal(p.dirDeg)}</span>` : '');
    tip.hidden = false;
    // Keep the readout off the crosshair so the cursor dot stays visible.
    positionChartTip(svg, wrap, tip, tx, { top: PAD.top, bottom: PAD.top + PLOT_H });
  };

  trackChartPointer(svg, {
    onMove: showAt,
    onEnd: () => {
      crosshair.setAttribute('opacity', '0');
      cursor.setAttribute('opacity', '0');
      tip.hidden = true;
    },
  });

  host.append(wrap);

  const legend = document.createElement('p');
  legend.className = 'chart-legend';
  legend.innerHTML =
    `<span class="legend-item"><i class="swatch-line" style="background:${SERIES_1}"></i>Combined sea</span>` +
    `<span class="legend-item"><i class="swatch-line" style="background:${INK_MUTED}"></i>Swell only</span>`;
  host.append(legend);

  // CC BY 4.0 requires the credit to sit next to the data itself, not only in a
  // footer somewhere else on the page.
  const credit = document.createElement('p');
  credit.className = 'chart-credit';
  credit.innerHTML =
    'Wave data by <a href="https://open-meteo.com/" target="_blank" rel="noopener">Open-Meteo.com</a>';
  host.append(credit);
}
